import { combineReducers, configureStore } from '@reduxjs/toolkit';
import sharedReducer, { sharedInitialState } from '@/pages/shared/shared.reducer';
import { documentFiltersReducer } from '@/common/document-filters/document-filters-reducer';
import { contactDetailsReducer } from '@/common/user-profile/user-profile-reducer';
import accountReducer from '@/pages/accounts/account.reducer';
import loginReducer from '@/pages/login/login.reducer';
import accountDetailsReducer from '@/common/account-details/account-details.reducer';
import siteReducer from '@/common/site-details/site.reducer';
import approvalSummaryReducer from '@/pages/approval-summary/approval-summary.reducer';
import cartManagementReducer, { cartPersistenceMiddleware } from '@/pages/cart-management/cart-management.slice';
import checkerSummaryReducer from '@/pages/checker-summary/checker-summary.reducer';
import numberTypeReducer from '@/pages/document-number-type/reducer';
import updateMetadataReducer from '@/pages/update-metadata/reducer';

// Import the dashboard API and dashboard slice
import { dashboardApi } from '@/pages/dashboard/dashboard.reducer';
import { dashboardSliceReducer, dashboardInitialState } from '@/pages/dashboard/dashboard.slice';
import { secretMigrationApi } from '@/pages/secret-migration/secret-migration.query';
import { documentFiltersApi } from '@/common/document-filters/document-filters.query';
import { sharedApi } from '@/pages/shared/shared.query';
import { checkerSummaryApi } from '@/pages/checker-summary/checker-summary.query';
import { approvalSummaryApi } from '@/pages/approval-summary/approval-summary.query';
import { deletedDocumentsApi } from '@/pages/deleted-documents/deleted-documents.query';
import { reportsApi } from '@/pages/reports/reports.query';
import { aboutApi } from '@/pages/about/about.query';
import { exportDocumentApi } from '@/pages/export-document/export-document.query';
import { documentNumberTypeApi } from '@/pages/document-number-type/document-number-type.query';
import { numberTypeFieldApi } from '@/pages/document-number-type-field-summary/number-type-field.query';
import { staffApi } from '@/pages/staff/staff.query';
import { accountApi } from '@/pages/accounts/account.query';
import { userProfileApi } from '@/common/user-profile/user-profile.query';
import { siteApi } from '@/common/site-details/site.query';
import { accountDetailsApi } from '@/common/account-details/account-details.query';
import { loginApi } from '@/pages/login/login.query';
import { loginAuthApi } from '@/pages/login/login-auth.query';
import { utilitiesApi } from '@/pages/shared/utilities.query';
import { revisionRollbackApi } from '@/pages/revision-rollback/revision-rollback.query';
import { updateMetadataApi } from '@/pages/update-metadata/update-metadata.query';
import { systemOutageApi } from '@/common/system-outage/system-outage.query';
import { dynamicMiddleware, getLazyReducers, setLazyReducersListener } from '@/store-registry';

// Upload Documents, Job Summary and Critical Equipment state is not listed here: those slices and
// APIs register through store-registry when their route or panel chunk loads (AB#2926).
const staticReducers = {
  documentFilters: documentFiltersReducer,
  contactDetails: contactDetailsReducer,
  shared: sharedReducer,
  account: accountReducer,
  userDetails: loginReducer,
  accountDetails: accountDetailsReducer,
  siteDetails: siteReducer,
  approvalSummary: approvalSummaryReducer,
  cartManagement: cartManagementReducer,
  checkerSummary: checkerSummaryReducer,
  numberType: numberTypeReducer,
  updateMetadata: updateMetadataReducer,
  dashboardPage: dashboardSliceReducer,
  [dashboardApi.reducerPath]: dashboardApi.reducer, 
  [secretMigrationApi.reducerPath]: secretMigrationApi.reducer,
  [documentFiltersApi.reducerPath]: documentFiltersApi.reducer,
  [sharedApi.reducerPath]: sharedApi.reducer,
  [checkerSummaryApi.reducerPath]: checkerSummaryApi.reducer,
  [approvalSummaryApi.reducerPath]: approvalSummaryApi.reducer,
  [deletedDocumentsApi.reducerPath]: deletedDocumentsApi.reducer,
  [reportsApi.reducerPath]: reportsApi.reducer,
  [aboutApi.reducerPath]: aboutApi.reducer,
  [exportDocumentApi.reducerPath]: exportDocumentApi.reducer,
  [documentNumberTypeApi.reducerPath]: documentNumberTypeApi.reducer,
  [numberTypeFieldApi.reducerPath]: numberTypeFieldApi.reducer,
  [staffApi.reducerPath]: staffApi.reducer,
  [accountApi.reducerPath]: accountApi.reducer,
  [userProfileApi.reducerPath]: userProfileApi.reducer,
  [siteApi.reducerPath]: siteApi.reducer,
  [accountDetailsApi.reducerPath]: accountDetailsApi.reducer,
  [loginApi.reducerPath]: loginApi.reducer,
  [loginAuthApi.reducerPath]: loginAuthApi.reducer,
  [utilitiesApi.reducerPath]: utilitiesApi.reducer,
  [revisionRollbackApi.reducerPath]: revisionRollbackApi.reducer,
  [updateMetadataApi.reducerPath]: updateMetadataApi.reducer,
  [systemOutageApi.reducerPath]: systemOutageApi.reducer,
};

const buildRootReducer = () => combineReducers({ ...staticReducers, ...getLazyReducers() });
let rootReducer = buildRootReducer();

const customRootReducer = (state, action) => {
  if (action.type === 'CLEAR') {
    state = {
      // Keys left out here (including registered lazy slices such as uploadDocuments and
      // criticalEquipment) are reset to their initial state by their reducers.
      userDetails: state.userDetails,
      dashboard: {
        ...dashboardInitialState,
        dashboardPage: {
          sites: state.dashboardPage.sites,
        },
      },
      cartManagement: {
        dashboardCartDocumentIds: state.cartManagement.dashboardCartDocumentIds,
        approvalCartDocumentIds: state.cartManagement.approvalCartDocumentIds,
      },
      shared: {
        ...sharedInitialState,
        siteId: state.shared.siteId,
        accessToAllSitesDashboard: state.shared.accessToAllSitesDashboard,
        accountLogo: state.shared.accountLogo,
        dashboardTable: {
          filters: {
            siteId: action.payload && action.payload.clearSiteFilter ? undefined : state.shared.siteId,
            documentStatusId: 1,
          },
        },
        cartManagementTable: {
          filters: {
            siteId: state.shared.siteId,
            documentStatusId: 1,
          },
        },
        approvalSummaryTable: {
          filters: {
            siteId: state.shared.siteId,
          },
        },
        checkerSummaryTable: {
          filters: {
            siteId: state.shared.siteId,
          },
        },
        jobSummaryTable: {
          filters: {
            siteId: state.shared.siteId,
          },
        },
        reportTable: {
          filters: {
            siteId: state.shared.siteId,
          },
        },
        criticalEquipmentDashboardTable: {
          filters: {
            siteId: state.shared.siteId,
          },
        },
        allSitesDashboard: {
          filters: {
            documentStatusId: 1,
          },
        },
        numberTypeFieldSummaryTable: {
          filters: {
            siteId: state.shared.siteId,
          },
        },
        documentNumberTypeTable: {
          filters: {
            siteId: state.shared.siteId,
          },
        },
        deletedDocumentsTable: {
          filters: {
            siteId: state.shared.siteId,
          },
        },
      },
    };
  }

  return rootReducer(state, action);
};

const store = configureStore({
  reducer: customRootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          'dashboardApi/executeMutation/pending',
          'dashboardApi/executeMutation/fulfilled',
          'dashboardApi/executeMutation/rejected',
          'dashboardApi/executeQuery/fulfilled',
          'dashboardApi/executeQuery/rejected',
          'dashboardApi/executeQuery/pending',
        ],
        // Ignore the RTK Query cache paths
        ignoredPaths: ['dashboardApi.mutations', 'dashboardApi.queries'],
      },
    }).concat(
      dashboardApi.middleware, 
      secretMigrationApi.middleware,
      documentFiltersApi.middleware,
      sharedApi.middleware,
      checkerSummaryApi.middleware,
      approvalSummaryApi.middleware,
      deletedDocumentsApi.middleware,
      reportsApi.middleware,
      aboutApi.middleware,
      exportDocumentApi.middleware,
      documentNumberTypeApi.middleware,
      numberTypeFieldApi.middleware,
      staffApi.middleware,
      accountApi.middleware,
      userProfileApi.middleware,
      siteApi.middleware,
      accountDetailsApi.middleware,
      loginApi.middleware,
      loginAuthApi.middleware,
      utilitiesApi.middleware,
      revisionRollbackApi.middleware,
      updateMetadataApi.middleware,
      systemOutageApi.middleware,
      cartPersistenceMiddleware,
      dynamicMiddleware.middleware
    ), 
  devTools: process.env.NODE_ENV !== 'production',
});

setLazyReducersListener(() => {
  rootReducer = buildRootReducer();
  store.replaceReducer(customRootReducer);
});
// Pick up anything that registered while this module was still evaluating.
if (Object.keys(getLazyReducers()).length > 0) {
  rootReducer = buildRootReducer();
  store.replaceReducer(customRootReducer);
}

export default store;
